import { useState } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Play, 
  Trash2, 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  Database,
  Rocket,
  ClipboardList,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { seedTestProject, cleanupTestProjects, verifyDatabaseAccess, createTestInvitation } from '../utils/testDataSeeder';

interface TestResult {
  step: string;
  status: 'pending' | 'running' | 'passed' | 'failed';
  message?: string;
  details?: any;
}

const STAGES = [
  { id: 'stage-1', name: 'Initiation', path: 'stage-1' },
  { id: 'stage-2', name: 'Data Import', path: 'stage-2' },
  { id: 'stage-3', name: 'Validation', path: 'stage-3' },
  { id: 'stage-4', name: 'Mapping', path: 'stage-4' },
  { id: 'stage-5', name: 'Review', path: 'stage-5' },
  { id: 'stage-6', name: 'Sign-Off', path: 'stage-6' },
  { id: 'stage-7', name: 'Go-Live', path: 'stage-7' },
];

export default function E2ETestPanel() {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [isRunning, setIsRunning] = useState(false);
  const [customerName, setCustomerName] = useState('E2E Test Company');
  const [includeStages, setIncludeStages] = useState(false);
  const [includeConfigs, setIncludeConfigs] = useState(true);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('create');

  const updateResult = (step: string, update: Partial<TestResult>) => {
    setTestResults(prev => 
      prev.map(r => r.step === step ? { ...r, ...update } : r)
    );
  };

  const runE2ETest = async () => {
    if (!currentWorkspace) {
      toast({ title: 'Error', description: 'No workspace selected', variant: 'destructive' });
      return;
    }

    setIsRunning(true);
    setCreatedProjectId(null);
    setTestResults([
      { step: 'verify_db', status: 'pending' },
      { step: 'create_project', status: 'pending' },
      { step: 'verify_data', status: 'pending' },
    ]);

    try {
      // Step 1: Verify database access
      updateResult('verify_db', { status: 'running' });
      const dbCheck = await verifyDatabaseAccess();
      updateResult('verify_db', {
        status: dbCheck.success ? 'passed' : 'failed',
        message: dbCheck.success 
          ? `${dbCheck.tables.length} tables accessible` 
          : `${dbCheck.errors.length} errors`,
        details: dbCheck,
      });

      if (!dbCheck.success) {
        toast({ title: 'Database Error', description: 'Cannot access required tables', variant: 'destructive' });
        setIsRunning(false);
        return;
      }

      // Step 2: Create test project
      updateResult('create_project', { status: 'running' });
      const result = await seedTestProject(currentWorkspace.id, {
        customerName: `${customerName} ${new Date().toISOString().split('T')[0]}`,
        includeStages,
        includeConfigs,
      });

      setCreatedProjectId(result.project.id);
      updateResult('create_project', {
        status: 'passed',
        message: `Project "${result.project.customer_name}" created`,
        details: {
          projectId: result.project.id,
          batches: result.batches.length,
          stagedData: result.stagedData.length,
          signOffs: result.signOffs.length,
          configurations: result.configurations.length,
        },
      });

      // Step 3: Verify data
      updateResult('verify_data', { status: 'running' });
      updateResult('verify_data', {
        status: 'passed',
        message: `${result.stagedData.length} accounts staged, ${result.configurations.length} configs`,
      });

      toast({
        title: 'Test Data Created',
        description: `Project "${result.project.customer_name}" is ready for testing`,
      });

    } catch (error: any) {
      console.error('[E2ETestPanel] E2E Test Error:', error);
      toast({ title: 'Test Failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsRunning(false);
    }
  };

  const cleanupTests = async () => {
    if (!currentWorkspace) return;

    try {
      setIsRunning(true);
      const count = await cleanupTestProjects(currentWorkspace.id, 'E2E Test');
      toast({
        title: 'Cleanup Complete',
        description: `Removed ${count} test project(s)`,
      });
      setCreatedProjectId(null);
      setTestResults([]);
    } catch (error: any) {
      toast({ title: 'Cleanup Failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsRunning(false);
    }
  };

  const navigateToStage = (stagePath: string) => {
    if (createdProjectId && currentWorkspace) {
      navigate(`/w/${currentWorkspace.id}/dashboard/tum/onboarding/project/${createdProjectId}/${stagePath}`);
    }
  };

  const createInvitation = async () => {
    if (!createdProjectId || !currentWorkspace) return;

    try {
      setIsRunning(true);
      const invitation = await createTestInvitation(createdProjectId, currentWorkspace.id);
      toast({
        title: 'Invitation Created',
        description: `Token: ${invitation.token}`,
      });
      // Open customer portal in new tab
      window.open(`/onboarding/invite/${invitation.token}`, '_blank');
    } catch (error: any) {
      toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Card className="border-dashed border-2 border-yellow-500/50 bg-yellow-50/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-yellow-600" />
          E2E Test Panel
          <Badge variant="outline" className="ml-2 text-yellow-600 border-yellow-600">
            Testing Tools
          </Badge>
        </CardTitle>
        <CardDescription>
          Create test data and walk through the complete 7-stage onboarding workflow
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="create">Create Test Data</TabsTrigger>
            <TabsTrigger value="stages" disabled={!createdProjectId}>Stage Navigation</TabsTrigger>
            <TabsTrigger value="results">Results</TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="space-y-4">
            {/* Configuration */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customerName">Customer Name Prefix</Label>
                <Input
                  id="customerName"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="E2E Test Company"
                />
              </div>
              <div className="space-y-3 pt-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="includeConfigs"
                    checked={includeConfigs}
                    onCheckedChange={(checked) => setIncludeConfigs(!!checked)}
                  />
                  <Label htmlFor="includeConfigs" className="text-sm">Include account configurations</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="includeStages"
                    checked={includeStages}
                    onCheckedChange={(checked) => setIncludeStages(!!checked)}
                  />
                  <Label htmlFor="includeStages" className="text-sm">Auto-approve stages (for go-live testing)</Label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Button onClick={runE2ETest} disabled={isRunning}>
                {isRunning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Create Test Data
              </Button>
              <Button variant="outline" onClick={cleanupTests} disabled={isRunning}>
                <Trash2 className="h-4 w-4 mr-2" />
                Cleanup
              </Button>
              {createdProjectId && (
                <>
                  <Button variant="secondary" onClick={() => navigateToStage('stage-2')}>
                    <Rocket className="h-4 w-4 mr-2" />
                    Go to Data Import
                  </Button>
                  <Button variant="outline" onClick={createInvitation} disabled={isRunning}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Create Invitation
                  </Button>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="stages" className="space-y-4">
            {createdProjectId ? (
              <div className="space-y-2">
                <Label>Navigate to Stage</Label>
                <div className="grid grid-cols-1 gap-2">
                  {STAGES.map((stage, index) => (
                    <Button
                      key={stage.id}
                      variant="outline"
                      className="justify-between"
                      onClick={() => navigateToStage(stage.path)}
                    >
                      <span className="flex items-center gap-2">
                        <span className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        {stage.name}
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <Alert>
                <Database className="h-4 w-4" />
                <AlertDescription>
                  Create test data first to enable stage navigation
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          <TabsContent value="results" className="space-y-4">
            {testResults.length > 0 ? (
              <div className="space-y-2">
                {testResults.map((result) => (
                  <div
                    key={result.step}
                    className="flex items-center gap-3 p-2 rounded border bg-background"
                  >
                    {result.status === 'pending' && (
                      <div className="h-4 w-4 rounded-full bg-muted" />
                    )}
                    {result.status === 'running' && (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                    )}
                    {result.status === 'passed' && (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    )}
                    {result.status === 'failed' && (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <div className="flex-1">
                      <span className="font-medium capitalize">
                        {result.step.replace(/_/g, ' ')}
                      </span>
                      {result.message && (
                        <span className="text-muted-foreground ml-2">
                          — {result.message}
                        </span>
                      )}
                    </div>
                  </div>
                ))}

                {createdProjectId && (
                  <Alert className="mt-4">
                    <Database className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Project ID:</strong> {createdProjectId}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No test results yet. Create test data to see results.
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}